import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getToken, logout, getUserId } from "../utils/auth";
import "./StaffMCQList.css";

export default function StaffMCQList() {
  const [mcqs, setMcqs] = useState([]); 
  const [staff, setStaff] = useState(null);
  const navigate = useNavigate();
  const userId = getUserId();

  useEffect(() => {
    async function fetchStaffAndMCQs() {
      try {
        const staffRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/getStaffById/${userId}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );

        if (!staffRes.ok) {
          logout();
          navigate("/login");
          return;
        }

        const staffData = await staffRes.json();
        
        if (!staffData) {
          console.error("Staff record not found in database for ID:", userId);
          logout();
          navigate("/login");
          return;
        }

        setStaff(staffData); 

        if (staffData.college) {
          const collegeMcqRes = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/getDistinctMCQbyCollege?college=${encodeURIComponent(staffData.college)}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );

          if (collegeMcqRes.ok) {
            const responseData = await collegeMcqRes.json();
            
            if (responseData && responseData.success && Array.isArray(responseData.data)) {
              const flattenedMcqs = responseData.data
                .filter(item => item && item.mcqDetails) 
                .map(item => item.mcqDetails);          
                
              setMcqs(flattenedMcqs);
            } else {
              setMcqs([]);
            }
          } else {
            console.error("Failed to retrieve distinct college MCQs");
          }
        }
      } catch (err) {
        console.error("Error running the MCQ fetch pipeline:", err);
      }
    }

    if (userId) {
      fetchStaffAndMCQs();
    } else {
      navigate("/login");
    }
  }, [userId, navigate]);

  if (!staff) {
    return (
      <div className="crm-loading-wrapper">
        <div className="crm-spinner"></div>
        <p>Retrieving structural registry...</p>
      </div>
    );
  }

  return (
    <div className="crm-dashboard">
      <header className="crm-topbar">
        <div className="crm-topbar-left">
          <span className="crm-breadcrumbs">Management / Assessment Analytics</span>
          <h1 className="crm-main-title">Academic Assessments</h1>
          <p className="crm-subtitle">
            Enterprise testing roster mapped directly to <strong className="crm-institution">{staff.college}</strong>
          </p>
        </div>
        <div className="crm-topbar-right">
          <div className="crm-counter-pill">
            <span className="pill-label">Total Active Modules</span>
            <span className="pill-value">{mcqs.length}</span>
          </div>
        </div>
      </header>

      {mcqs.length === 0 ? (
        <div className="crm-empty-panel">
          <div className="empty-panel-inner">
            <h3>No Assessment Pipelines Assigned</h3>
            <p>This institution currently has zero active evaluation tracks registered in the central database.</p>
          </div>
        </div>
      ) : (
        <div className="crm-table-container">
          <div className="crm-table-header">
            <div className="th-topic">Module & Topic</div>
            <div className="th-category">Classification</div>
            <div className="th-metrics">Metrics</div>
            <div className="th-action">Actions</div>
          </div>

          <div className="crm-table-body">
            {mcqs.map((m) => (
              <div
                key={m._id}
                className="crm-table-row"
                onClick={() =>
                  navigate("/staff/mcq-report", {
                    state: { mcqId: m._id, college: staff.college }
                  })
                }
              >
                <div className="td-topic">
                  <span className="row-badge">MCQ</span>
                  <span className="main-topic-title">{m.topic}</span>
                </div>

                <div className="td-category">
                  <span className="category-tag">{m.category || "General"}</span>
                </div>

                <div className="td-metrics">
                  <div className="metric-cell">
                    <span className="metric-num">{m.questions?.length || 0}</span>
                    <span className="metric-lbl">Items</span>
                  </div>
                  <div className="metric-separator"></div>
                  <div className="metric-cell">
                    <span className="metric-num">
                      {m.duration ? `${m.duration}m` : "Untimed"}
                    </span>
                    <span className="metric-lbl">Duration</span>
                  </div>
                </div>

                <div className="td-action">
                  <button className="crm-action-button">
                    <span>Analyze Records</span>
                    <svg className="btn-arrow" viewBox="0 0 24 24" width="16" height="16">
                      <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}